import { createSlice } from '@reduxjs/toolkit';

import { IPagination } from 'types';
import { AssetsManagementState } from './types';

export const initialState: AssetsManagementState = {
  videoList: {} as AssetsManagementState['videoList'],
  allVideos: {} as AssetsManagementState['allVideos'],
  loading: false,
  error: '',
  placements: {} as AssetsManagementState['placements'],
  tagList: [],
  forms: [],
  playlist: undefined,
  playlistContents: [],
  feedContents: [],
  pinnedVideos: [],
  feedContentPage: {} as IPagination,
  placement: undefined,
  assetDetails: {},
  channels: {},
  streams: {},
  playlists: {},
  placementList: {},
  placementPlaylistMapper: {},
  selectedPlacementId: '',
  placementVideoLoading: false,
  placementToggling: false,
  tagSyncingLoading: false,
  placementVideoError: '',
  disableVideoloading: false,
  drawerType: undefined,
  selectedVideo: {},
  selectedVideos: [],
  activeIndex: 0,
  searchVideosLoading: false,
  searchVideosError: ''
};

const localeSlice = createSlice({
  name: 'assetsManagement',
  initialState,
  reducers: {
    fetchVideoList: (state, payload) => {
      state.loading = true;
      state.error = '';
    },
    fetchVideoListSuccess: (state, action) => {
      state.loading = false;
      state.videoList = action.payload;
    },
    fetchVideoListFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    fetchAllVideos: (state, payload) => {
      state.loading = true;
      state.error = '';
    },
    fetchAllVideosSuccess: (state, action) => {
      state.loading = false;
      state.allVideos = action.payload;
    },
    fetchAllVideosFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    updateUpload: (state, action) => {
      state.loading = true;
      state.error = '';
    },
    updateUploadSuccess: state => {
      state.loading = false;
      state.error = '';
    },
    updateUploadFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    getChannelsByMarketerId: (state, action) => {
      state.placementVideoLoading = true;
      state.placementVideoError = '';
    },
    getChannelsByMarketerIdSuccess: (state, action) => {
      state.placementVideoLoading = false;
      state.placementVideoError = '';

      state.channels = action.payload.channels;
      state.streams = action.payload.stream;
      state.placementList = action.payload.placementList;
      state.playlists = action.payload.playlistList;
      state.placementPlaylistMapper = action.payload.placementPlaylistMapper;

      // {
      //   'channels' : response.data._embedded.channels,
      //   'streams' : streams,
      //   'placementList' : placementList,
      //   'playlistList' : playlistList
      // }
      // state = {
      //   ...state ,
      //   loading : false,
      //   error : '',
      //   'channels' : action.payload.channels,
      //   'streams' : action.payload.stream,
      //   'placements' : action.payload.placementList,
      //   'playlists' : action.payload.playlistList
      // };
    },
    getChannelsByMarketerIdFailure: (state, action) => {
      state.placementVideoLoading = true;
      state.placementVideoError = action.payload;
    },
    updatePlacementTags: (state, action) => {
      state.tagSyncingLoading = true;
      state.placementVideoError = '';
    },
    updatePlacementTagsSuccess: (state, action) => {
      state.tagSyncingLoading = false;
      state.placementList = { ...state.placementList, [action.payload.id]: action.payload };
      state.placementVideoError = '';
    },
    updatePlacementTagsFailure: (state, action) => {
      state.tagSyncingLoading = false;
      state.placementVideoError = action.payload;
    },
    getTagsByMarketer: (state, action) => {
      // state.loading = true;
      state.error = '';
    },
    getTagsByMarketerSuccess: (state, action) => {
      // state.loading = false;
      state.error = '';
      state.tagList = action.payload;
    },
    getTagsByMarketerFailure: (state, action) => {
      // state.loading = true;
      state.error = action.payload;
    },
    disableVideo: (state, action) => {
      state.disableVideoloading = true;
      state.error = '';
    },
    disableVideoSuccess: state => {
      state.disableVideoloading = false;
      state.error = '';
    },
    disableVideoFailure: (state, action) => {
      state.disableVideoloading = false;
      state.error = action.payload;
    },
    fetchForms: (state, action) => {
      // state.loading = true;
      state.error = '';
    },
    fetchFormsSuccess: (state, action) => {
      // state.loading = false;
      state.forms = action.payload;
    },
    fetchFormsFailure: (state, action) => {
      // state.loading = false;
      state.error = action.payload;
    },
    resetState: () => {
      return initialState;
    },
    createPlaylist: (state, action) => {
      state.loading = true;
      state.error = '';
    },
    createPlaylistSuccess: (state, action) => {
      state.loading = false;
      state.playlist = action.payload;
    },
    createPlaylistFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    linkFeedsToPlaylist: (state, action) => {
      state.loading = true;
      state.error = '';
    },
    linkFeedsToPlaylistSuccess: (state, action) => {
      state.placementVideoLoading = false;
      state.playlist = action.payload;
    },
    linkFeedsToPlaylistFailure: (state, action) => {
      state.placementVideoLoading = false;
      state.placementVideoError = action.payload;
    },
    fetchContentsByPlaylist: (state, action) => {
      state.placementVideoLoading = true;
      state.placementVideoError = '';
    },
    fetchContentsByPlaylistSuccess: (state, action) => {
      state.loading = false;
      state.playlistContents = action.payload;
    },
    fetchContentsByPlaylistFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    linkPlaylistsToContent: (state, action) => {
      state.placementToggling = true;
      state.placementVideoError = '';
    },
    linkPlaylistsToContentSuccess: (state, action) => {
      state.placementToggling = false;

      state.videoList._embedded.contents = state.videoList._embedded.contents.map((item: any) =>
        item.id === action.payload.id ? { ...item, ...action.payload } : item
      );
    },
    linkPlaylistsToContentFailure: (state, action) => {
      state.placementToggling = false;
      // state.placementVideoError = action.payload;
    },

    linkPlaylistsToListOfContents: (state, action) => {
      state.placementVideoLoading = true;
      state.placementVideoError = '';
    },
    linkPlaylistsToListOfContentsSuccess: (state, action) => {
      state.placementVideoLoading = false;
      state.videoList._embedded.contents = state.videoList._embedded.contents.map((item: any) => {
        const existingElement = action.payload.find((data: any) => data.id === item.id);
        return existingElement ? { ...item, ...existingElement } : item;
      });

      // state.videoList._embedded.contents = state.videoList._embedded.contents.map((item : any) =>
      //   item.id === action.payload.id
      //     ? { ...item, ...action.payload }
      //     : item
      // );
    },
    linkPlaylistsToListOfContentsFailure: (state, action) => {
      state.placementVideoLoading = false;
      state.placementVideoError = action.payload;
    },

    fetchPlaylistByFeedId: (state, action) => {
      state.loading = true;
      state.error = '';
    },
    fetchPlaylistByFeedIdSuccess: (state, action) => {
      state.loading = false;
      state.placement = action.payload;
    },
    fetchPlaylistByFeedIdFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    fetchPinnedVideoPlayListForPlacement: (state, action) => {
      state.placementVideoLoading = true;
      state.placementVideoError = '';
    },
    fetchPinnedVideoPlayListForPlacementSuccess: (state, action) => {
      state.placementVideoLoading = false;
      state.pinnedVideos = action.payload;
    },
    fetchPinnedVideoPlayListForPlacementFailure: (state, action) => {
      state.placementVideoLoading = false;
      state.placementVideoError = action.payload;
    },
    findOrCreatePlaylistByFeedId: (state, action) => {
      state.loading = true;
      state.error = '';
    },
    findOrCreatePlaylistByFeedIdSuccess: (state, action) => {
      state.loading = false;
      state.playlist = action.payload;
    },
    findOrCreatePlaylistByFeedIdFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    searchVideosInPlaylist: (state, action) => {
      state.searchVideosLoading = true;
      state.searchVideosError = '';
    },
    searchVideosInPlaylistSuccess: (state, action) => {
      state.searchVideosLoading = false;
      state.searchVideosError = '';
    },
    searchVideosInPlaylistFailure: (state, action) => {
      state.searchVideosLoading = false;
      state.searchVideosError = action.payload;
    },
    fetchContentForFeed: (state, action) => {
      state.loading = true;
      state.error = '';
    },
    fetchContentForFeedSuccess: (state, action) => {
      state.loading = false;
      state.error = '';
      state.feedContents = action.payload;
    },
    fetchContentForFeedFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    fetchVideoPlayListForPlacement: (state, action) => {
      state.placementVideoLoading = true;
      state.placementVideoError = '';
    },
    fetchVideoPlayListForPlacementSuccess: (state, action) => {
      state.placementVideoLoading = false;
      state.placementVideoError = '';
      state.feedContents = action.payload.content;
      state.feedContentPage = action.payload.page;
    },
    fetchVideoPlayListForPlacementFailure: (state, action) => {
      state.placementVideoLoading = false;
      state.placementVideoError = action.payload;
    },
    unlinkContentFromPlaylist: (state, action) => {
      state.placementToggling = true;
      state.placementVideoError = '';
    },
    unlinkContentFromPlaylistSuccess: (state, action) => {
      state.placementToggling = false;
      state.videoList._embedded.contents = state.videoList._embedded.contents.map((item: any) =>
        item.id === action.payload.id ? { ...item, ...action.payload } : item
      );
      state.placementVideoError = '';
    },
    unlinkContentFromPlaylistFailure: (state, action) => {
      state.placementToggling = false;
      state.placementVideoError = action.payload;
    },
    fetchAssetDetails: (state, action) => {
      state.loading = true;
      state.error = '';
    },
    fetchAssetDetailsSuccess: (state, action) => {
      state.loading = false;
      const { assetId, data, video } = action.payload;
      // Merge video object into asset details if provided
      state.assetDetails[assetId] = {
        ...(data || {}),
        ...(video ? { video } : {})
      };
    },
    fetchAssetDetailsFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    reorderPlaylistContent: (state, action) => {
      state.placementVideoLoading = true;
      state.placementVideoError = '';
    },
    reorderPlaylistContentSuccess: state => {
      state.placementVideoLoading = false;
      state.placementVideoError = '';
      // state.feedContents = state.playlistContents;
    },
    reorderPlaylistContentFailure: (state, action) => {
      state.placementVideoLoading = false;
      state.placementVideoError = action.payload;
    },
    pinContentToPlaylist: (state, action) => {
      state.placementToggling = true;
      state.placementVideoError = '';
    },
    pinContentToPlaylistSuccess: state => {
      state.placementToggling = false;
      state.placementVideoError = '';
    },
    pinContentToPlaylistFailure: (state, action) => {
      state.placementToggling = false;
      state.placementVideoError = action.payload;
    },
    unpinContentFromPlaylist: (state, action) => {
      state.placementToggling = true;
      state.placementVideoError = '';
    },
    unpinContentFromPlaylistSuccess: state => {
      state.placementToggling = false;
      state.placementVideoError = '';
    },
    unpinContentFromPlaylistFailure: (state, action) => {
      state.placementToggling = false;
      state.placementVideoError = action.payload;
    },
    fetchPinnedPlaylistContent: (state, action) => {
      state.loading = true;
      state.error = '';
    },
    fetchPinnedPlaylistContentSuccess: (state, action) => {
      state.loading = false;
      state.error = '';
      state.playlistContents = action.payload;
    },
    fetchPinnedPlaylistContentFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    fetchUnpinnedPlaylistContent: (state, action) => {
      state.loading = true;
      state.error = '';
    },
    fetchUnpinnedPlaylistContentSuccess: (state, action) => {
      state.loading = false;
      state.error = '';
      state.unpinnedPlaylistContents = action.payload;
    },
    fetchUnpinnedPlaylistContentFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    updateSelectedPlacement: (state, action) => {
      state.selectedPlacementId = action.payload;
      if (!action.payload) {
        state.feedContents = [];
      }
    },

    setDrawerType: (state, action) => {
      state.drawerType = action.payload;
    },
    setSelectedVideo: (state, action) => {
      state.selectedVideo = action.payload;
    },

    setSelectedVideos: (state, action) => {
      state.selectedVideos = action.payload;
    },
    setActiveIndex: (state, action) => {
      state.activeIndex = action.payload;
    },

    removePinnedContentFromPlaylist: (state, action) => {
      state.placementToggling = true;
      state.placementVideoError = '';
    },
    removePinnedContentFromPlaylistSuccess: (state, action) => {
      state.placementToggling = false;
      state.videoList._embedded.contents = state.videoList._embedded.contents.map((item: any) =>
        item.id === action.payload.id ? { ...item, ...action.payload } : item
      );
      state.placementVideoError = '';
    },
    removePinnedContentFromPlaylistFailure: (state, action) => {
      state.placementToggling = false;
      state.placementVideoError = action.payload;
    }
  }
});

export const {
  fetchVideoList,
  fetchVideoListSuccess,
  fetchVideoListFailure,
  fetchAllVideos,
  fetchAllVideosSuccess,
  fetchAllVideosFailure,
  updateUpload,
  updateUploadSuccess,
  updateUploadFailure,
  getChannelsByMarketerId,
  getChannelsByMarketerIdSuccess,
  getChannelsByMarketerIdFailure,
  updatePlacementTags,
  updatePlacementTagsSuccess,
  updatePlacementTagsFailure,
  getTagsByMarketer,
  getTagsByMarketerSuccess,
  getTagsByMarketerFailure,
  disableVideo,
  disableVideoSuccess,
  disableVideoFailure,
  fetchForms,
  fetchFormsSuccess,
  fetchFormsFailure,
  resetState,
  createPlaylist,
  createPlaylistSuccess,
  createPlaylistFailure,
  linkFeedsToPlaylist,
  linkFeedsToPlaylistSuccess,
  linkFeedsToPlaylistFailure,
  fetchContentsByPlaylist,
  fetchContentsByPlaylistSuccess,
  fetchContentsByPlaylistFailure,
  linkPlaylistsToContent,
  linkPlaylistsToContentSuccess,
  linkPlaylistsToContentFailure,
  linkPlaylistsToListOfContents,
  linkPlaylistsToListOfContentsSuccess,
  linkPlaylistsToListOfContentsFailure,
  fetchPlaylistByFeedId,
  fetchPlaylistByFeedIdSuccess,
  fetchPlaylistByFeedIdFailure,
  fetchPinnedVideoPlayListForPlacement,
  fetchPinnedVideoPlayListForPlacementSuccess,
  fetchPinnedVideoPlayListForPlacementFailure,
  findOrCreatePlaylistByFeedId,
  findOrCreatePlaylistByFeedIdSuccess,
  findOrCreatePlaylistByFeedIdFailure,
  fetchContentForFeed,
  fetchContentForFeedSuccess,
  fetchContentForFeedFailure,
  searchVideosInPlaylist,
  searchVideosInPlaylistSuccess,
  searchVideosInPlaylistFailure,
  fetchVideoPlayListForPlacement,
  fetchVideoPlayListForPlacementSuccess,
  fetchVideoPlayListForPlacementFailure,
  unlinkContentFromPlaylist,
  unlinkContentFromPlaylistSuccess,
  unlinkContentFromPlaylistFailure,
  fetchAssetDetails,
  fetchAssetDetailsSuccess,
  fetchAssetDetailsFailure,
  reorderPlaylistContent,
  reorderPlaylistContentSuccess,
  reorderPlaylistContentFailure,
  pinContentToPlaylist,
  pinContentToPlaylistSuccess,
  pinContentToPlaylistFailure,
  unpinContentFromPlaylist,
  unpinContentFromPlaylistSuccess,
  unpinContentFromPlaylistFailure,
  fetchPinnedPlaylistContent,
  fetchPinnedPlaylistContentSuccess,
  fetchPinnedPlaylistContentFailure,
  fetchUnpinnedPlaylistContent,
  fetchUnpinnedPlaylistContentSuccess,
  fetchUnpinnedPlaylistContentFailure,
  updateSelectedPlacement,
  setDrawerType,
  setSelectedVideo,
  setSelectedVideos,
  setActiveIndex,
  removePinnedContentFromPlaylist,
  removePinnedContentFromPlaylistSuccess,
  removePinnedContentFromPlaylistFailure
} = localeSlice.actions;

export default localeSlice.reducer;
